import { hasWindow } from "@omni/utils/dom.utils";
import { isEnum } from "@omni/utils/enum.utils";
import { isDefined } from "@omni/utils/helper.utils";

import { CookieSetEvent } from "@/services/windowEvents/events/cookieSet.event";

export enum Cookie {
    ECornerId = "ECORNER_ID",
    ECornerStoreId = "ECORNER_STORE_ID",
    ECornerTerminalId = "ECORNER_TERMINAL_ID",
    Language = "HB_LANGUAGE",
    ReviewsFeedback = "HB_REVIEWS_FEEDBACK",
    Ga = "_ga"
}

const POLL_INTERVAL_MS = 1000;

const lastSeen = new Map<Cookie, string>();

const hasCookieStore = () => hasWindow() && Boolean(window.cookieStore);

const readDocumentCookie = (name: Cookie) => {
    const prefix = `${name}=`;

    return document.cookie
        .split("; ")
        .find((entry) => entry.startsWith(prefix))
        ?.slice(prefix.length);
};

const writeDocumentCookie = (name: Cookie, value: string, expires?: Date) => {
    const attributes = [`path=/`, `domain=.${LIB_NAME}.be`];
    if (expires) attributes.push(`expires=${expires.toUTCString()}`);

    document.cookie = `${name}=${value}; ${attributes.join("; ")}`;
};

export const get = async (name: Cookie) => {
    if (!hasWindow()) return;

    if (!hasCookieStore()) return readDocumentCookie(name);

    return window.cookieStore.get(name).then((cookie) => {
        return cookie?.value;
    });
};

export const set = async (name: Cookie, value: string, days?: number) => {
    if (!hasWindow()) return;

    const expires = days ? Date.now() + days * 24 * 60 * 60 * 1000 : undefined;

    if (!hasCookieStore()) {
        writeDocumentCookie(
            name,
            value,
            expires ? new Date(expires) : undefined
        );
        lastSeen.set(name, value);
        window.dispatchEvent(new CookieSetEvent(name, value));
        return;
    }

    return window.cookieStore.set({
        name: name,
        value: value,
        expires: expires,
        path: "/",
        domain: `.${LIB_NAME}.be`
    });
};

export const remove = async (name: Cookie) => {
    if (!hasWindow()) return;

    if (!hasCookieStore()) {
        writeDocumentCookie(name, "", new Date(0));
        lastSeen.delete(name);
        return;
    }

    return window.cookieStore.delete(name);
};

export const setLanguageCookie = (languageCode: string) => {
    return set(Cookie.Language, languageCode, 30);
};

const _handleOnCookieChange = (event: CookieChangeEvent) => {
    event.changed.forEach((cookie) => {
        if (!isEnum(Cookie, cookie.name)) return;
        if (!isDefined(cookie.value)) return;

        window.dispatchEvent(new CookieSetEvent(cookie.name, cookie.value));
    });
};

const _pollDocumentCookies = () => {
    Object.values(Cookie).forEach((name) => {
        const value = readDocumentCookie(name);
        if (!isDefined(value)) {
            lastSeen.delete(name);
            return;
        }
        if (lastSeen.get(name) === value) return;

        lastSeen.set(name, value);
        window.dispatchEvent(new CookieSetEvent(name, value));
    });
};

(() => {
    if (!hasWindow()) return;

    if (hasCookieStore()) {
        window.cookieStore.addEventListener("change", _handleOnCookieChange);
        return;
    }

    Object.values(Cookie).forEach((name) => {
        const value = readDocumentCookie(name);
        if (isDefined(value)) lastSeen.set(name, value);
    });
    window.setInterval(_pollDocumentCookies, POLL_INTERVAL_MS);
})();
